/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Routing for ServiceAccess login
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.11.0
 */

import { useEffect } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { createStructuredSelector } from 'reselect';

import { appHistoryPush } from '@/main/modules/app/appHistory';
import { IRootReducer } from '@/main/reducers/rootReducer';
import PageAuthProvider from '@pages/PageAuth/PageAuthProvider';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectLoggedIn,
    makeSelectNewsServiceAccessLoggedIn,
    makeSelectServiceAccessLoggedIn,
} from '@pages/PageAuth/selectors/authSelectors';
import PageAuthServiceAccess from '@pages/PageAuthServiceAccess/Loadable';

import { RoutePath } from '../constants/routesConstants';

export const LoginServiceAccessRoute = (props: RouteComponentProps & IConnectedProps) => {
    const { loggedIn, isNewsServiceAccessLoggedIn, isServiceAccessLoggedIn, isServiceUser } = props;

    useEffect(() => {
        // Пользователь авторизован как новостной сервисный пользователь
        if (loggedIn && isNewsServiceAccessLoggedIn) {
            appHistoryPush(RoutePath.news);
            return;
        }
        // Пользователь авторизован как абонент и (прошел 2 шаг авторизации СП или он не СП)
        if (loggedIn && (isServiceAccessLoggedIn || !isServiceUser)) {
            appHistoryPush(RoutePath.main);
            return;
        }
    }, [isNewsServiceAccessLoggedIn, isServiceAccessLoggedIn, isServiceUser, loggedIn]);

    return (
        <PageAuthProvider isServiceUser={true}>
            <PageAuthServiceAccess isServiceAccessLoggedIn={isServiceAccessLoggedIn} />
        </PageAuthProvider>
    );
};

export interface IConnectedProps {
    isServiceAccessLoggedIn: boolean;
    isNewsServiceAccessLoggedIn: boolean;
    isServiceUser: boolean;
    loggedIn: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, RouteComponentProps, IConnectedProps>({
    isServiceAccessLoggedIn: makeSelectServiceAccessLoggedIn(),
    isNewsServiceAccessLoggedIn: makeSelectNewsServiceAccessLoggedIn(),
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
    loggedIn: makeSelectLoggedIn(),
});

export default connect(mapStateToProps)(LoginServiceAccessRoute);
